/**
 * All gameplay, physics, combat, network and reward tuning for Thrasher lives here so the
 * client simulation and the authoritative server always agree on the same numbers.
 */

export const PROTOCOL_VERSION = 2;

export const RIDES = ['skateboard', 'bmx', 'inline'] as const;
export type Ride = (typeof RIDES)[number];

export interface RideTuning {
  label: string;
  blurb: string;
  /** Top speed reachable by pushing/pedalling on flat ground (m/s). */
  pushSpeed: number;
  /** Acceleration while pushing (m/s²). */
  pushAccel: number;
  /** Carve rate at low speed (rad/s). */
  turnRate: number;
  /** Carve rate at high speed (rad/s). */
  turnRateFast: number;
  /** Ollie / bunny-hop launch speed (m/s). */
  ollie: number;
  /** Fully charged ollie launch speed (m/s). */
  chargedOllie: number;
  /** Air spin rate (rad/s). */
  spinRate: number;
  /** Seconds to complete a flip-type trick. */
  flipTime: number;
  /** Deceleration while grinding (m/s²). */
  grindFriction: number;
  /** Multiplier on rail balance instability. */
  balanceDrift: number;
  /** Rolling resistance on the ground (m/s²). */
  rollingDrag: number;
  /** Height of the rider's feet above the grind line (m). */
  grindOffset: number;
}

export const RIDE_TUNING: Record<Ride, RideTuning> = {
  skateboard: {
    label: 'Skateboard',
    blurb: 'Balanced carving, big flips, classic grinds.',
    pushSpeed: 12.5,
    pushAccel: 7,
    turnRate: 2.9,
    turnRateFast: 1.7,
    ollie: 7.2,
    chargedOllie: 9.6,
    spinRate: 7.4,
    flipTime: 0.42,
    grindFriction: 0.6,
    balanceDrift: 1,
    rollingDrag: 0.25,
    grindOffset: 0.12,
  },
  bmx: {
    label: 'BMX',
    blurb: 'Fastest pedal speed, huge hops, steady pegs.',
    pushSpeed: 14.5,
    pushAccel: 8.2,
    turnRate: 2.6,
    turnRateFast: 1.5,
    ollie: 7.6,
    chargedOllie: 10,
    spinRate: 6.8,
    flipTime: 0.5,
    grindFriction: 0.8,
    balanceDrift: 0.8,
    rollingDrag: 0.2,
    grindOffset: 0.34,
  },
  inline: {
    label: 'Inline Skates',
    blurb: 'Tightest turns, fastest spins, slippery grinds.',
    pushSpeed: 13.5,
    pushAccel: 8,
    turnRate: 3.4,
    turnRateFast: 1.95,
    ollie: 7,
    chargedOllie: 9.2,
    spinRate: 8.4,
    flipTime: 0.38,
    grindFriction: 0.5,
    balanceDrift: 1.1,
    rollingDrag: 0.22,
    grindOffset: 0.1,
  },
};

export const PHYSICS = {
  gravity: 22,
  fixedStep: 1 / 120,
  maxFrameDelta: 0.1,
  /** Absolute speed cap for the local rider (m/s). */
  maxSpeed: 32,
  /** Quadratic air drag coefficient. */
  airDrag: 0.0014,
  /** How strongly sideways slip is removed on the ground (1/s). */
  lateralGrip: 14,
  brakeDecel: 10,
  /** Extra acceleration down transitions while crouched (m/s²). */
  pumpAccel: 2.6,
  /**
   * Arcade assist: gravity along transitions (ramps, pools) is softened in both directions,
   * so riders reach lips without creating free energy; pumping adds speed.
   */
  transitionGravityScale: 0.6,
  /** Ground probe starts this far above the feet along the surface normal. */
  probeLift: 0.3,
  /** Highest step the rider rolls up without jumping (m). */
  stepUp: 0.2,
  /** How far below the feet the ground probe still sticks (m). */
  snapDown: 0.4,
  /** Minimum per-step gap before a convex edge launches the rider (m). */
  launchGap: 0.03,
  bodyRadius: 0.42,
  bodyHeight: 0.95,
  footRadius: 0.28,
  footHeight: 0.36,
  /** Leaving a surface whose normal.y is below this counts as a vert launch. */
  vertNormalY: 0.42,
  /** Distance inside the lip a returning vert air hovers at (m). */
  vertInset: 0.32,
  /** Outward speed added when holding forward at a vert lip (m/s). */
  vertExitBoost: 2.8,
  vertExitSpeedScale: 0.1,
  /** Minimum upward speed to leave a vert lip rather than roll back (m/s). */
  vertMinExitSpeed: 1.2,
  grindSnapRadius: 0.8,
  grindSnapBelow: 0.75,
  grindSnapAbove: 0.5,
  grindMinSpeed: 4.5,
  /** cos(max angle between travel and rail) that still locks a grind. */
  grindAlignDot: 0.45,
  balanceGain: 2.4,
  balanceCorrection: 7.5,
  balanceNoise: 2,
  balanceNoiseGrowth: 0.22,
  balanceDamping: 1.1,
  railExitLift: 1.5,
  railHopLateral: 2.6,
  bailSeconds: 1.5,
  wallBailSpeed: 16,
  /** A landing within this many degrees of forward or fakie is clean. */
  landingToleranceDeg: 58,
  chargeSeconds: 0.6,
  jumpBufferSeconds: 0.14,
  coyoteSeconds: 0.12,
  trickCooldownSeconds: 0.12,
  /** Bail if a flip is less than this fraction complete on landing. */
  flipLandFraction: 0.8,
} as const;

export const COMBAT = {
  maxHp: 100,
  attacks: {
    punch: {
      damage: 20,
      range: 2.3,
      cooldownMs: 420,
      knockback: 5.5,
      lift: 2.5,
      balanceShock: 0.45,
      animMs: 260,
    },
    kick: {
      damage: 34,
      range: 2.8,
      cooldownMs: 760,
      knockback: 10.5,
      lift: 4.5,
      balanceShock: 0.85,
      animMs: 380,
    },
  },
  /** cos(75°): the target must be within this cone in front of the attacker. */
  facingDot: 0.26,
  maxVerticalReach: 2.2,
  regenDelayMs: 4_000,
  regenPerSecond: 12,
  koRespawnMs: 3_000,
  spawnProtectionMs: 3_000,
  /** Attack checks also test target positions this far in the past. */
  rewindMs: 150,
} as const;

export type AttackKind = keyof typeof COMBAT.attacks;
export const ATTACK_KINDS: readonly AttackKind[] = ['punch', 'kick'];

export const WORLD = {
  maxRiders: 16,
  snapshotHz: 20,
  clientStateHz: 20,
  interpolationDelayMs: 120,
  halfExtent: 180,
  minY: -25,
  maxY: 90,
  /** Fastest reported travel the server accepts (m/s). */
  maxReportedSpeed: 48,
  /** Extra distance allowed per state report on top of the speed envelope (m). */
  positionSlack: 3,
  resetCooldownMs: 2_500,
  nameMaxLength: 20,
} as const;

export const RATE_LIMITS = {
  statePerSecond: 45,
  attacksPerSecond: 6,
  ridePerTenSeconds: 5,
  resetPerTenSeconds: 4,
  helloPerTenSeconds: 5,
} as const;

export const REWARDS = {
  flushIntervalMs: 180_000,
  /** Riders moving faster than this (m/s) accrue active time. */
  activeSpeed: 1.5,
  coinsPerActiveMinute: 3,
  coinsPerKnockout: 2,
  maxCoinsPerFlush: 40,
  /** A session is only credited once the rider has been active this long. */
  minActiveSecondsForCredit: 60,
} as const;

/** Rider accent colours; the server assigns the least-used index. */
export const RIDER_COLORS = [
  '#ff4d3d',
  '#2ec4ff',
  '#ffd23f',
  '#7cff4f',
  '#ff5fd2',
  '#a66bff',
  '#ff9a2e',
  '#2effc7',
  '#f2f2f2',
  '#3d6bff',
  '#ff2e7a',
  '#c8ff2e',
  '#2e9bff',
  '#ffb3a0',
  '#8a5a2e',
  '#5ef0ff',
] as const;

/** Server pilots: distances in metres, speeds in m/s, and timers in milliseconds. */
export const BOTS = {
  /** Total riders to maintain while at least one human is present; zero disables bots. */
  fillTo: 6,
  names: [
    'Rivet',
    'Scuff',
    'Noodle',
    'Grit',
    'Zip',
    'Pebble',
    'Clank',
    'Wobble',
    'Sprocket',
    'Puddle',
    'Fidget',
    'Crumb',
  ],
  aggroRadius: 65,
  maxChasers: 2,
  /** Chance, rolled once per reconsider window, to brawl another bot instead. */
  botTargetChance: 0.2,
  targetReconsiderMs: 4_000,
  retaliationMs: 6_000,
  roamRadius: 24,
  arenaInset: 15,
  waypointArrival: 4,
  waypointTimeoutMs: 6_000,
  predictionSeconds: 0.3,
  steerGain: 1.6,
  pushSpeedFraction: 0.75,
  matchSpeedRadius: 8,
  approachSpeed: 2,
  speedMatchSlack: 0.5,
  attackIntervalMs: 1_100,
  reactionMinMs: 350,
  reactionMaxMs: 800,
  kickChance: 0.35,
  /** Charge occasional hops, then attempt at most one trick on the ascent. */
  trickMinMs: 5_000,
  trickMaxMs: 12_000,
  jumpMinMs: 150,
  jumpMaxMs: 500,
  trickMinRiseSpeed: 4,
  airFlipChance: 0.3,
  airGrabChance: 0.35,
  grabMs: 180,
  /** Low-speed escape first; teleport only after prolonged lack of displacement. */
  stuckSpeed: 1.5,
  stuckMs: 2_500,
  escapeMs: 800,
  recoveryProgress: 3,
  recoveryMs: 12_000,
  recoveryMinY: -20,
  /** Match the client's crouch pose threshold. */
  crouchCharge: 0.05,
} as const;
